import { serviceManager } from '@/services/service-manager/service-manager.service';

export interface Device {
  id: string;
  name: string;
  vendorId: number;
  productId: number;
  manufacturer?: string;
  product?: string;
  connected?: boolean;
}

export interface CardReadEvent {
  data: string;
  timestamp: number;
  deviceId?: string;
}

export class DeviceService {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private listeners: Map<string, Set<(...args: any[]) => void>> = new Map();
  private reconnectTimeout: number | null = null;

  constructor() {
    // Auto-connect when service is available
    this.attemptConnection();
  }

  async connect(): Promise<void> {
    // Check if service is running first
    const status = await serviceManager.checkServiceStatus();
    if (!status.running) {
      throw new Error('Device Bridge Service is not running. Please install and start the service.');
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    const wsUrl = serviceManager.getWebSocketUrl();

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.emit('connected');
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleMessage(data);
        } catch (error) {
          // Ignore parse errors
        }
      };

      this.ws.onerror = (error) => {
        this.emit('error', error);
      };

      this.ws.onclose = () => {
        this.emit('disconnected');
        this.attemptReconnect();
      };
    } catch (error) {
      // Connection failed - will retry
      this.attemptReconnect();
    }
  }

  private attemptConnection(): void {
    // Try to connect, but don't throw if service is not available
    this.connect().catch(() => {
      // Service not available, will retry when user starts it
    });
  }

  private attemptReconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      this.reconnectTimeout = window.setTimeout(() => {
        this.attemptConnection();
      }, this.reconnectDelay * this.reconnectAttempts);
    } else {
      this.emit('connectionFailed');
    }
  }

  private handleMessage(data: any): void {
    switch (data.type) {
      case 'CARD_DATA':
        this.emit('cardRead', {
          data: data.data,
          timestamp: data.timestamp,
          deviceId: data.deviceId
        });
        break;

      case 'READ_STARTED':
        this.emit('readStarted', {
          deviceId: data.deviceId,
          message: data.message
        });
        break;

      case 'DEVICE_LIST':
        this.emit('deviceList', data.devices);
        break;

      case 'DEVICE_CONNECTED':
        this.emit('deviceConnected', data.device);
        break;

      case 'DEVICE_DISCONNECTED':
        this.emit('deviceDisconnected', data.deviceId);
        break;

      case 'CONNECTED':
        this.emit('serviceConnected', data);
        break;

      case 'ERROR':
        this.emit('error', new Error(data.message));
        break;

      case 'PONG':
        // Heartbeat response
        break;

      default:
      // Unknown message type - ignore
    }
  }

  private emit(event: string, ...args: any[]): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.forEach((listener) => {
        try {
          listener(...args);
        } catch (error) {
          // Ignore listener errors
        }
      });
    }
  }

  on(event: 'cardRead', listener: (event: CardReadEvent) => void): void;
  on(event: 'readStarted', listener: (event: { deviceId?: string; message?: string }) => void): void;
  on(event: 'deviceList', listener: (devices: Device[]) => void): void;
  on(event: 'deviceConnected', listener: (device: Device) => void): void;
  on(event: 'deviceDisconnected', listener: (deviceId: string) => void): void;
  on(event: 'connected' | 'disconnected' | 'connectionFailed' | 'serviceConnected', listener: () => void): void;
  on(event: 'error', listener: (error: Error) => void): void;
  on(event: string, listener: (...args: any[]) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(listener);
  }

  off(event: string, listener: (...args: any[]) => void): void {
    const eventListeners = this.listeners.get(event);
    if (eventListeners) {
      eventListeners.delete(listener);
    }
  }

  discoverDevices(): void {
    this.send({ type: 'DISCOVER_DEVICES' });
  }

  readCard(deviceId?: string): void {
    this.send({ type: 'READ_CARD', deviceId });
  }

  connectDevice(deviceId: string): void {
    this.send({ type: 'CONNECT_DEVICE', deviceId });
  }

  disconnectDevice(deviceId: string): void {
    this.send({ type: 'DISCONNECT_DEVICE', deviceId });
  }

  getStatus(deviceId?: string): void {
    this.send({ type: 'GET_STATUS', deviceId });
  }

  ping(): void {
    this.send({ type: 'PING' });
  }

  private send(data: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(data));
    } else {
      this.connect().then(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify(data));
        }
      }).catch((error) => {
        // Connection failed - ignore
      });
    }
  }

  disconnect(): void {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.listeners.clear();
  }

  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}

export const deviceService = new DeviceService();

